import React from 'react';
import type { RenderFn } from '@atlassian/forge-ui-types';
import { withConfluenceStyleWrapper } from '../confluenceStyleWrapper';
import { getPlotMargin, type MarginValue } from '../getPlotMargin';
import { fg } from '@atlaskit/platform-feature-flags';

const PlatformStackBarChart = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_stackbarchart' */
		'@atlassian/viz-platform-charts/charts'
	).then((module) => ({ default: module.StackBarChart })),
);

const Title = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_stackbarchart' */
		'@atlassian/viz-platform-charts/charts'
	).then((module) => ({ default: module.StackBarChart.elements.Title })),
);

const SubTitle = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_stackbarchart' */
		'@atlassian/viz-platform-charts/charts'
	).then((module) => ({ default: module.StackBarChart.elements.SubTitle })),
);

const Layout = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_barchart' */
		'@atlassian/viz-platform-charts/charts'
	).then((module) => ({ default: module.StackBarChart.elements.Layout })),
);
const Theme = React.lazy(() =>
	// eslint-disable-next-line import/dynamic-import-chunkname
	import(
		/* webpackChunkName: '@forge-ui_barchart' */
		'@atlassian/viz-platform-charts/charts'
	).then((module) => ({ default: module.StackBarChart.elements.Theme })),
);

type PlatformStackBarChartProps = React.ComponentProps<typeof PlatformStackBarChart>;
/**
 * Excluded props:
 * - children - we will not be supporting primitives/elements to compose the chart
 * - colorPalette - allow the user to match color to attribute
 *
 * Limited props:
 * - xAccessor - only allow non function types
 * - yAccessor - only allow non function types
 * - colorAccessor - only allow non function types
 */
export type StackBarChartProps = Pick<PlatformStackBarChartProps, 'width' | 'height' | 'data'> & {
	colorAccessor: number | string;
	plotMargin?: MarginValue;
	plotMarginBottom?: MarginValue;
	plotMarginLeft?: MarginValue;
	plotMarginRight?: MarginValue;
	plotMarginTop?: MarginValue;
	showBorder?: boolean;
	subtitle?: string;
	title?: string;
	xAccessor: number | string;
	yAccessor: number | string;
	colorPalette?: string[] | { key: string; value: string }[];
};
const BaseStackBarChart = (props: Parameters<RenderFn>[0]) => {
	const {
		width,
		height,
		data,
		xAccessor,
		yAccessor,
		colorAccessor,
		title,
		subtitle,
		plotMargin,
		plotMarginTop,
		plotMarginRight,
		plotMarginBottom,
		plotMarginLeft,
		showBorder,
		colorPalette,
	} = props.forgeDoc.props as StackBarChartProps;

	return (
		<PlatformStackBarChart
			width={width}
			height={height ? height : 400}
			data={data}
			xAccessor={xAccessor}
			yAccessor={yAccessor}
			colorAccessor={colorAccessor}
		>
			{title && <Title>{title}</Title>}
			{subtitle && <SubTitle>{subtitle}</SubTitle>}
			{fg('enable-margin-property-for-ui-kit-chart-components') && (
				<Layout
					plotMargin={getPlotMargin(plotMargin, plotMarginLeft, plotMarginRight, plotMarginTop, plotMarginBottom)}
					showBorder={showBorder}
				/>
			)}
			{colorPalette && fg('platform-enable-customisation-colors-for-charts') && (
				<Theme fillStyles={colorPalette} />
			)}
		</PlatformStackBarChart>
	);
};

export const StackBarChart: (props: Parameters<RenderFn>[0]) => JSX.Element =
	withConfluenceStyleWrapper(BaseStackBarChart);
